import { useState, useMemo } from "react";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import axios from "axios";
import { z } from "zod";
import { Spinner } from "./ui/spinner";
import { API_BASE_URL } from "../lib/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/I18nProvider";
import { AlertCircle, CheckCircle2, Check, X } from "lucide-react";

export function SignUpForm({ className }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Password strength checker
  const passwordStrength = useMemo(() => {
    const pwd = formData.password;
    return {
      minLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
    };
  }, [formData.password]);

  // Enhanced Zod schema with stricter password requirements
  const signUpSchema = z
    .object({
      name: z.string().min(1, { message: t('auth.signUpPage.nameRequired') }),
      email: z.string().email({ message: t('auth.signUpPage.emailInvalid') }),
      password: z
        .string()
        .min(8, { message: "Password must be at least 8 characters long" })
        .regex(/[A-Z]/, { message: "Password must contain an uppercase letter" })
        .regex(/[a-z]/, { message: "Password must contain a lowercase letter" })
        .regex(/\d/, { message: "Password must contain a number" })
        .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, { message: "Password must contain a special character" }),
      confirmPassword: z.string().min(8, {
        message: "Confirm password must be at least 8 characters long",
      }),
      agreeToTerms: z.boolean().refine(val => val === true, {
        message: "You must agree to the Terms of Service and Privacy Policy",
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.signUpPage.passwordMismatch'),
      path: ["confirmPassword"],
    });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "email" ? value.toLowerCase() : value,
      });
    }
  };

  const navigate = useNavigate();

  // Check email uniqueness
  const checkEmailUniqueness = async (email) => {
    if (!email || !z.string().email().safeParse(email).success) {
      setEmailError("");
      return;
    }

    setEmailCheckLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/check-email`,
        { email: email.toLowerCase() }
      );
      if (response.data.exists) {
        setEmailError("This email is already registered");
      } else {
        setEmailError("");
      }
    } catch (error) {
      // Email check failed, let form submission handle validation
      setEmailError("");
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors and alert message
    setErrors({});
    setAlertMessage("");

    // Check email error
    if (emailError) {
      setErrors({ email: [emailError] });
      return;
    }

    // Validate data using Zod
    const result = signUpSchema.safeParse(formData);
    if (!result.success) {
      // Set validation errors if any
      const newErrors = result.error.formErrors.fieldErrors;
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      // Make API request
      const response = await axios.post(
        API_BASE_URL + "/api/auth/sign-up",
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
        }
      );

      if (response.status === 201) {
        setAlertMessage(t('auth.signUpPage.successMessage'));
        setAlertType("success");
        setLoading(false);

        setTimeout(() => {
          navigate(
            `/sign-up/otp-verification?email=${response.data.user.email}`
          );
        }, 1000);
      } else {
        setAlertMessage(response.data.message);
        setAlertType("error");
        setLoading(false);
      }
    } catch (error) {
      setAlertMessage(
        error.response?.data?.error || error.message || t('auth.signUpPage.errorMessage')
      );
      setAlertType("destructive");
      setLoading(false);
    }
  };

  return (
    <Card className={cn(
      className, 
      "w-full max-w-md mx-auto shadow-lg border border-gray-200 dark:border-gray-700"
    )}>
      <CardHeader className="space-y-2 bg-gradient-to-br from-indigo-50/50 to-white dark:from-indigo-900/20 dark:to-gray-900 rounded-t-lg">
        <CardTitle className="text-2xl font-bold text-center text-gray-900 dark:text-white">
          {t('auth.signUpPage.title')}
        </CardTitle>
        <CardDescription className="text-center text-gray-600 dark:text-gray-400">
          {t('auth.signUpPage.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="name"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.signUpPage.nameLabel')}
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder={t('auth.signUpPage.namePlaceholder')}
              value={formData.name}
              onChange={handleChange}
              required
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                errors.name && "border-red-500 focus:ring-red-500"
              )}
            />
            {errors.name && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name[0]}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="email"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.signUpPage.emailLabel')}
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t('auth.signUpPage.emailPlaceholder')}
              value={formData.email}
              onChange={handleChange}
              onBlur={(e) => checkEmailUniqueness(e.target.value)}
              required
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                (errors.email || emailError) && "border-red-500 focus:ring-red-500"
              )}
            />
            {emailCheckLoading && (
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Spinner className="w-4 h-4" />
                Checking email...
              </p>
            )}
            {(errors.email || emailError) && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} />
                <p style={{ color: '#EF4444' }} className="text-sm font-medium">
                  {errors.email?.[0] || emailError}
                </p>
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="password"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.signUpPage.passwordLabel')}
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                errors.password && "border-red-500 focus:ring-red-500"
              )}
            />
            {errors.password && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password[0]}
              </p>
            )}
            
            {/* Password Strength Checklist */}
            {formData.password && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">Password requirements:</p>
                <div className="space-y-1 text-xs">
                  <div className={`flex items-center gap-2 ${passwordStrength.minLength ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {passwordStrength.minLength ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    At least 8 characters
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.hasUppercase ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {passwordStrength.hasUppercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Contains uppercase letter
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.hasLowercase ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {passwordStrength.hasLowercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Contains lowercase letter
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.hasNumber ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {passwordStrength.hasNumber ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Contains number
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.hasSpecialChar ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {passwordStrength.hasSpecialChar ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Contains special character (!@#$%^&*)
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-2">
            <Label 
              htmlFor="confirm_password"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              {t('auth.signUpPage.confirmPasswordLabel')}
            </Label>
            <Input
              id="confirm_password"
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className={cn(
                "rounded-lg border-gray-200 dark:border-gray-700",
                errors.confirmPassword && "border-red-500 focus:ring-red-500",
                formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && "border-green-500"
              )}
            />
            {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
              <p className="text-green-600 dark:text-green-400 text-sm font-medium flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Passwords match
              </p>
            )}
            {errors.confirmPassword && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.confirmPassword[0]}
              </p>
            )}
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="space-y-2 pt-2">
            <div className="flex items-start gap-3">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 mt-0.5"
              />
              <label htmlFor="agreeToTerms" className="text-sm text-gray-700 dark:text-gray-300">
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 underline font-medium">
                  Terms of Service
                </a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 underline font-medium">
                  Privacy Policy
                </a>
              </label>
            </div>
            {errors.agreeToTerms && (
              <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1 ml-8">
                <AlertCircle className="w-4 h-4" />
                {errors.agreeToTerms[0]}
              </p>
            )}
          </div>

          {/* Alerts */}
          {alertMessage && alertType === "success" ? (
            <Alert className="rounded-lg border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle className="text-green-800 dark:text-green-300">Success</AlertTitle>
              <AlertDescription className="text-green-700 dark:text-green-200">{alertMessage}</AlertDescription>
            </Alert>
          ) : alertMessage ? (
            <Alert variant="destructive" className="rounded-lg border-red-500 bg-red-50">
              <AlertCircle className="h-4 w-4" style={{ color: '#EF4444' }} />
              <AlertTitle style={{ color: '#EF4444' }} className="font-medium">Error</AlertTitle>
              <AlertDescription style={{ color: '#EF4444' }}>{alertMessage}</AlertDescription>
            </Alert>
          ) : null}

          {/* Submit Button - Disabled until form is valid */}
          <Button
            type="submit"
            className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-semibold py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.agreeToTerms}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Spinner className="w-4 h-4" />
                {t('auth.signUpPage.createButton')}...
              </div>
            ) : (
              t('auth.signUpPage.createButton')
            )}
          </Button>

          {/* Login Link */}
          <div className="pt-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Have an account?{" "}
              <a
                href="/get-started?tab=login"
                className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
              >
                Login
              </a>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
